import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../api/axios";
import "../assets/styles/page.css";
import "../assets/styles/ledger.css";

import {
  TbBook2,
  TbCalendarMonth,
  TbChevronLeft,
  TbChevronRight,
  TbFileInvoice,
  TbFileSpreadsheet,
  TbFileTypePdf,
  TbReceipt,
  TbReceiptRefund,
  TbShoppingBag,
  TbUser,
  TbWallet,
} from "react-icons/tb";

const PAGE_NAME = "Ledger";
const PAGE_SIZE = 25;

const getPermissionHeaders = (action) => ({
  "x-page-name": PAGE_NAME,
  "x-action": action,
});

const getCurrentMonth = () => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
  }).formatToParts(new Date());
  const part = (type) => parts.find((item) => item.type === type)?.value;
  return `${part("year")}-${part("month")}`;
};

const shiftMonth = (monthValue, amount) => {
  const [year, month] = monthValue.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1 + amount, 1));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
};

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
  }).format(Number(value || 0));

const getRefundLabel = (status) => {
  if (status === "partial") return "Partial refund";
  if (status === "full") return "Full refund";
  return "";
};

const formatDate = (value) => {
  if (!value) return "—";
  const [year, month, day] = String(value).split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
};

const showRequestError = (error, fallbackMessage) => {
  if (error.response?.status === 403) return;
  toast.error(error.response?.data?.message || fallbackMessage);
};

const getBlobErrorMessage = async (error, fallbackMessage) => {
  const responseData = error.response?.data;
  if (!(responseData instanceof Blob)) {
    return error.response?.data?.message || fallbackMessage;
  }

  try {
    const parsed = JSON.parse(await responseData.text());
    return parsed.message || fallbackMessage;
  } catch {
    return fallbackMessage;
  }
};

const Ledger = () => {
  const navigate = useNavigate();
  const [month, setMonth] = useState(getCurrentMonth);
  const [entries, setEntries] = useState([]);
  const [period, setPeriod] = useState(null);
  const [summary, setSummary] = useState({
    opening_balance: 0,
    total_debit: 0,
    total_credit: 0,
    closing_balance: 0,
    entry_count: 0,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    total: 0,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState("");

  const fetchLedger = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const response = await api.get("/admin/ledger", {
        params: { month, page, limit: PAGE_SIZE },
        headers: getPermissionHeaders("view"),
      });

      setEntries(response.data?.data || []);
      setPeriod(response.data?.period || null);
      setSummary(response.data?.summary || {});
      setPagination(response.data?.pagination || {
        page,
        total: 0,
        totalPages: 1,
      });
    } catch (error) {
      setEntries([]);
      showRequestError(error, "Failed to load the ledger.");
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    fetchLedger(1);
  }, [fetchLedger]);

  const summaryCards = useMemo(() => [
    {
      label: "Opening balance",
      value: summary.opening_balance,
      icon: <TbWallet />,
      tone: "neutral",
    },
    {
      label: "Invoices / Debit",
      value: summary.total_debit,
      icon: <TbReceipt />,
      tone: "debit",
    },
    {
      label: "Refunds / Credit",
      value: summary.total_credit,
      icon: <TbReceiptRefund />,
      tone: "credit",
    },
    {
      label: "Closing balance",
      value: summary.closing_balance,
      icon: <TbBook2 />,
      tone: "balance",
    },
  ], [summary]);

  const downloadExport = async (format) => {
    try {
      setDownloading(format);
      const response = await api.get(`/admin/ledger/export.${format}`, {
        params: { month },
        headers: getPermissionHeaders("view"),
        responseType: "blob",
        timeout: 0,
      });

      const mimeType = format === "xlsx"
        ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        : "application/pdf";
      const responseType = String(response.headers?.["content-type"] || "");
      if (responseType.includes("application/json")) {
        const message = JSON.parse(await response.data.text())?.message;
        throw new Error(message || "The export could not be generated.");
      }

      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `sales-ledger-${month}.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1500);
      toast.success(`${format === "xlsx" ? "Excel" : "PDF"} ledger downloaded.`);
    } catch (error) {
      if (error.response?.status === 403) return;
      const fallbackMessage = `Failed to download the ${format.toUpperCase()} ledger.`;
      const message = error.response
        ? await getBlobErrorMessage(error, fallbackMessage)
        : error.message || fallbackMessage;
      toast.error(message);
    } finally {
      setDownloading("");
    }
  };

  const goToPage = (page) => {
    if (page < 1 || page > pagination.totalPages || loading) return;
    fetchLedger(page);
  };

  return (
    <section className="admin-page ledger-page">
      <div className="page-header ledger-hero">
        <div>
          <p>Accounts</p>
          <h1>Sales Ledger</h1>
          <span>Invoices, refunds and running balances in one register.</span>
        </div>
        <div className="ledger-hero-icon" aria-hidden="true"><TbBook2 /></div>
      </div>

      <div className="ledger-toolbar page-card">
        <div className="ledger-month-control">
          <button
            type="button"
            aria-label="Previous month"
            onClick={() => setMonth((value) => shiftMonth(value, -1))}
          ><TbChevronLeft /></button>
          <label>
            <span><TbCalendarMonth /> Ledger month</span>
            <input
              type="month"
              value={month}
              max={getCurrentMonth()}
              onChange={(event) => event.target.value && setMonth(event.target.value)}
            />
          </label>
          <button
            type="button"
            aria-label="Next month"
            disabled={month >= getCurrentMonth()}
            onClick={() => setMonth((value) => shiftMonth(value, 1))}
          ><TbChevronRight /></button>
        </div>

        <div className="ledger-export-actions">
          <button
            type="button"
            className="ledger-export excel"
            disabled={!!downloading}
            onClick={() => downloadExport("xlsx")}
          ><TbFileSpreadsheet /> {downloading === "xlsx" ? "Preparing…" : "Download Excel"}</button>
          <button
            type="button"
            className="ledger-export pdf"
            disabled={!!downloading}
            onClick={() => downloadExport("pdf")}
          ><TbFileTypePdf /> {downloading === "pdf" ? "Preparing…" : "Download PDF"}</button>
        </div>
      </div>

      <div className="ledger-summary-grid">
        {summaryCards.map((card) => (
          <article className={`ledger-summary-card ${card.tone}`} key={card.label}>
            <span className="ledger-summary-icon">{card.icon}</span>
            <div><span>{card.label}</span><strong>{formatCurrency(card.value)}</strong></div>
          </article>
        ))}
      </div>

      <div className="page-card ledger-register">
        <header className="ledger-register-head">
          <div>
            <h2>{period?.label || "Monthly ledger"}</h2>
            <p>{loading ? "Loading entries…" : `${summary.entry_count || 0} transaction entries`}</p>
          </div>
          <div className="ledger-legend" aria-label="Ledger colour legend">
            <span className="ledger-legend-item"><i className="debit-dot" />Debit</span>
            <span className="ledger-legend-item"><i className="credit-dot" />Credit</span>
            <span className="ledger-legend-item"><i className="partial-dot" />Partial refund</span>
            <span className="ledger-legend-item"><i className="full-dot" />Full refund</span>
          </div>
        </header>

        <div className={`ledger-table-scroll ${loading ? "is-loading" : ""}`}>
          <table className="ledger-table">
            <thead><tr><th>Date</th><th>Type</th><th>Particulars</th><th>Reference</th><th>Debit</th><th>Credit</th><th>Balance</th><th>Shortcuts</th></tr></thead>
            <tbody>
              {pagination.page === 1 && (
                <tr className="ledger-opening-row">
                  <td>{formatDate(period?.start_date)}</td><td><span className="ledger-type opening">Opening</span></td><td>Balance brought forward</td><td>—</td><td>—</td><td>—</td><td className="ledger-money balance">{formatCurrency(summary.opening_balance)}</td><td>—</td>
                </tr>
              )}
              {!loading && entries.length === 0 && (
                <tr><td className="ledger-empty" colSpan="8"><TbBook2 /><strong>No ledger activity</strong><span>No invoices or completed refunds were recorded for this month.</span></td></tr>
              )}
              {entries.map((entry) => (
                <tr
                  className={`ledger-entry-row ${entry.entry_type} ${entry.entry_type === "debit" ? `refund-${entry.refund_status || "none"}` : "refund-none"}`}
                  key={entry.id}
                >
                  <td className="ledger-date">{formatDate(entry.date)}</td>
                  <td><span className={`ledger-type ${entry.entry_type}`}>{entry.document_type}</span></td>
                  <td className="ledger-particulars">
                    <strong>{entry.particulars}</strong>
                    {entry.remark && <span>{entry.remark}</span>}
                    {entry.entry_type === "debit" && entry.refund_status && entry.refund_status !== "none" && (
                      <span className={`ledger-refund-flag ${entry.refund_status}`}>
                        {getRefundLabel(entry.refund_status)}
                        {entry.refunded_amount ? ` · ${formatCurrency(entry.refunded_amount)}` : ""}
                      </span>
                    )}
                    <small>{entry.customer?.name || "Customer"}</small>
                  </td>
                  <td className="ledger-reference">{entry.reference}</td>
                  <td className="ledger-money debit-value">{entry.debit ? formatCurrency(entry.debit) : "—"}</td>
                  <td className="ledger-money credit-value">{entry.credit ? formatCurrency(entry.credit) : "—"}</td>
                  <td className="ledger-money balance">{formatCurrency(entry.balance)}</td>
                  <td>
                    <div className="ledger-shortcuts">
                      <button type="button" title="View order" aria-label={`View order ${entry.order?.order_number || entry.order?.order_id}`} onClick={() => navigate(`/orders?order_id=${entry.order?.order_id}`)}><TbShoppingBag /></button>
                      {entry.invoice?.invoice_id && <button type="button" title="View invoice" aria-label={`View invoice ${entry.invoice?.invoice_number}`} onClick={() => navigate(`/invoices?invoice_id=${entry.invoice.invoice_id}`)}><TbFileInvoice /></button>}
                      {entry.customer?.user_id && <button type="button" title="View customer" aria-label={`View customer ${entry.customer?.name}`} onClick={() => navigate(`/customers?user_id=${entry.customer.user_id}`)}><TbUser /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr><td colSpan="4">Period totals</td><td className="ledger-money">{formatCurrency(summary.total_debit)}</td><td className="ledger-money">{formatCurrency(summary.total_credit)}</td><td className="ledger-money">{formatCurrency(summary.closing_balance)}</td><td /></tr></tfoot>
          </table>
        </div>

        <footer className="ledger-pagination">
          <span>Showing {pagination.total ? (pagination.page - 1) * PAGE_SIZE + 1 : 0}–{Math.min(pagination.page * PAGE_SIZE, pagination.total || 0)} of {pagination.total || 0}</span>
          <div><button type="button" disabled={pagination.page <= 1 || loading} onClick={() => goToPage(pagination.page - 1)}><TbChevronLeft /> Previous</button><strong>Page {pagination.page} of {pagination.totalPages}</strong><button type="button" disabled={pagination.page >= pagination.totalPages || loading} onClick={() => goToPage(pagination.page + 1)}>Next <TbChevronRight /></button></div>
        </footer>
      </div>
    </section>
  );
};

export default Ledger;
